import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Wand2 } from "lucide-react";
import { toast } from "sonner";

// Admin: re-apply the current naming pattern to one project's existing photos.
export default function ApplyNamingToProject() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2 || picked) return setResults([]);
    const t = setTimeout(() => {
      api.get("/projects", { params: { q: q.trim() } }).then((r) => setResults(r.data.slice(0, 8))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, picked]);

  const label = (p) => [p.site_name, p.site_number, p.customer].filter(Boolean).join(" · ");

  const apply = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { data } = await api.post("/naming-patterns/apply", { project_id: picked.id });
      toast.success(`Renamed ${data.renamed} of ${data.total} photos on ${picked.site_name || picked.site_number}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-slate-800 pt-4 space-y-2" data-testid="naming-apply-section">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Rename an existing project's photos</div>
      <p className="text-[11px] text-slate-500">Uses that project's customer pattern (or the company default). Save your pattern first.</p>
      {picked ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-100" data-testid="naming-apply-picked">{label(picked)}</span>
          <button type="button" onClick={() => { setPicked(null); setQ(""); }} className="text-xs text-slate-400 underline" data-testid="naming-apply-clear">Change</button>
          <Button onClick={() => setConfirm(true)} disabled={busy} className="bg-amber-600 hover:bg-amber-500 ml-auto" data-testid="naming-apply-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}Rename photos
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search site name, number or customer" className="pl-8 bg-slate-950 border-slate-700" data-testid="naming-apply-search" />
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-700 bg-slate-900 shadow-xl">
              {results.map((p) => (
                <button key={p.id} type="button" onClick={() => setPicked(p)} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-800" data-testid={`naming-apply-result-${p.id}`}>
                  {label(p)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="naming-apply-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Every photo and video on {picked && label(picked)} gets renamed with the current pattern.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700" data-testid="naming-apply-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={apply} className="bg-amber-600 hover:bg-amber-500" data-testid="naming-apply-ok">Rename</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
